import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  TablePagination,
  useTheme,
} from "@mui/material";
import {
  GridCellParams,
  GridColDef,
  GridEventListener,
  GridPinnedColumnFields,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { HeatSeries } from "src/store/api/generatedApi";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterialDisplayName } from "hooks/useMaterials";
import { useTenantData } from "contexts/tenant";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { useDateRange } from "contexts/DateRangeContext";
import { ColouredCell } from "../ColouredCell";
import { ToggleSelector } from "../ToggleSelector";
import { SteelGradeCell } from "../heats/cells/SteelGradeCell";
import { RequireNonAggregationRow } from "../heats/RequireNonAggregationRow";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { ComplianceHeatsDataProvider } from "components/strategyDashboard/heats/context/ComplianceHeatsDataProvider";
import {
  HeatsSelectionProvider,
  useHeatsSelection,
} from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { HeatsFilterProvider } from "components/strategyDashboard/heats/context/HeatsFilterContext";
import { ChemistryElementProvider } from "components/strategyDashboard/heats/chemistryTab/ChemistryElementContext";
import HeatDetailsDrawer from "components/strategyDashboard/heats/HeatDetailsDrawer";
import { sorted } from "helpers";

const LEVEL2_SOURCE = "level2";
const PLANNED_SOURCE = "plannedSource";
const DEVIATION_THRESHOLD = 0.3;
const PAGE_SIZE = 50;

type MassUnit = "mass" | "small_mass";

const getMaterialMass = (
  heat: HeatSeries,
  materialName: string,
  source: string
): number | null => {
  const mix = heat.mixes.find((m) => m.source === source);
  if (!mix) return null;
  const materialTotal = mix.mix.totals.find(
    (t) => t.material_name === materialName
  );
  return materialTotal?.mass ?? 0;
};

const getTotalMass = (heat: HeatSeries, source: string): number => {
  const mix = heat.mixes.find((m) => m.source === source);
  if (!mix) return 0;
  return mix.mix.totals.reduce((sum, t) => sum + t.mass, 0);
};

const hasSignificantDeviation = (
  level2Mass: number | null,
  plannedMass: number | null
): boolean => {
  if (level2Mass === null || plannedMass === null) return false;
  if (plannedMass === 0) return level2Mass > 0;
  return Math.abs(level2Mass - plannedMass) / plannedMass > DEVIATION_THRESHOLD;
};

export const HeatComplianceTable = () => {
  return <HeatComplianceTableContent />;
};

const HeatComplianceTableContent = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.add(1, "day").startOf("day").format();
  const [page, setPage] = useState(0);
  const [massUnit, setMassUnit] = useState<MassUnit>("small_mass");

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets pagination on date change; user also navigates
    setPage(0);
  }, [start, end]);

  return (
    <ComplianceHeatsDataProvider
      start={start}
      end={end}
      page={page}
      resultsPerPage={PAGE_SIZE}
    >
      <HeatsSelectionProvider>
        <HeatsFilterProvider>
          <HeatComplianceTableInner
            page={page}
            onPageChange={setPage}
            massUnit={massUnit}
            onMassUnitChange={setMassUnit}
          />
        </HeatsFilterProvider>
      </HeatsSelectionProvider>
    </ComplianceHeatsDataProvider>
  );
};

const HeatComplianceTableInner = ({
  page,
  onPageChange,
  massUnit,
  onMassUnitChange,
}: {
  page: number;
  onPageChange: (page: number) => void;
  massUnit: MassUnit;
  onMassUnitChange: (unit: MassUnit) => void;
}) => {
  const { data, isFetching, isLoading, availableElements } = useHeatsData();
  const totalHeats = data?.total ?? 0;

  return (
    <ChemistryElementProvider availableElements={availableElements}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            pt: 1,
          }}
        >
          {isLoading || !data ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <HeatComplianceDataGrid
              heats={data.series}
              isFetching={isFetching}
              totalHeats={totalHeats}
              page={page}
              onPageChange={onPageChange}
              massUnit={massUnit}
              onMassUnitChange={onMassUnitChange}
            />
          )}
        </Box>
        <HeatDetailsDrawer />
      </Box>
    </ChemistryElementProvider>
  );
};

const PINNED_COLUMNS: GridPinnedColumnFields = { right: ["total_charged"] };
const SORT_MODEL: GridSortModel = [{ field: "heat_date", sort: "desc" }];

const HeatComplianceDataGrid = ({
  heats,
  isFetching,
  totalHeats,
  page,
  onPageChange,
  massUnit,
  onMassUnitChange,
}: {
  heats: HeatSeries[];
  isFetching: boolean;
  totalHeats: number;
  page: number;
  onPageChange: (page: number) => void;
  massUnit: MassUnit;
  onMassUnitChange: (unit: MassUnit) => void;
}) => {
  const { t } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const theme = useTheme();
  const { toLocalDatetime } = useLocalDatetime();
  const { setSelectedHeatId } = useHeatsSelection();
  const { materials } = useHeatsData();
  const { small_mass_per_mass: smallMassPerMass } = useTenantData();
  const units = useUnitsFormatter(false);
  const materialDisplayName = useMaterialDisplayName();

  const formatDate = useCallback(
    (date: string) => toLocalDatetime(date, "L"),
    [toLocalDatetime]
  );

  const convertMass = useCallback(
    (value: number) =>
      massUnit === "small_mass" ? value * smallMassPerMass : value,
    [massUnit, smallMassPerMass]
  );

  const formatMass = useCallback(
    (value: number | null) => {
      if (value === null) return "-";
      return format(convertMass(value), { decimalPlaces: 0 });
    },
    [format, convertMass]
  );

  const level2MaterialNames = useMemo(() => {
    const namesWithMass = new Set(
      heats.flatMap(
        (heat) =>
          heat.mixes
            .find((m) => m.source === LEVEL2_SOURCE)
            ?.mix.totals.filter((t) => t.mass > 0)
            .map((t) => t.material_name) ?? []
      )
    );
    if (materials) {
      return materials
        .filter((m) => namesWithMass.has(m.name))
        .map((m) => m.name);
    }
    return sorted(Array.from(namesWithMass), (n) => n);
  }, [heats, materials]);

  const columns: GridColDef<HeatSeries>[] = useMemo(() => {
    const cols: GridColDef<HeatSeries>[] = [
      {
        field: "heat_date",
        width: 100,
        headerName: t("date"),
        sortable: false,
        valueGetter: (_: unknown, row: HeatSeries) => row.start_timestamp,
        valueFormatter: (value: string | null) =>
          value ? formatDate(value) : null,
      },
      {
        field: "heat_id",
        width: 80,
        headerName: "#",
        sortable: false,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "crew_id",
        width: 60,
        headerName: t("crew"),
        sortable: false,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "steel_grade",
        minWidth: 100,
        headerName: t("steelGrade"),
        renderCell: RequireNonAggregationRow(({ row }) => (
          <SteelGradeCell
            steelGrade={row.steel_grade}
            originalSteelGrade={row.original_steel_grade}
          />
        )),
        sortable: false,
      },
    ];

    const materialColumns: GridColDef<HeatSeries>[] = level2MaterialNames.map(
      (materialName) => ({
        field: `material_${materialName}`,
        headerName: materialDisplayName(materialName),
        width: 90,
        headerAlign: "center" as const,
        align: "center" as const,
        sortable: false,
        cellClassName: "coloured-cell",
        valueGetter: (_: unknown, row: HeatSeries) =>
          getMaterialMass(row, materialName, LEVEL2_SOURCE),
        renderCell: (params: GridCellParams<HeatSeries>) => {
          const level2Mass = params.value as number | null;
          const plannedMass = getMaterialMass(
            params.row,
            materialName,
            PLANNED_SOURCE
          );
          const deviated = hasSignificantDeviation(level2Mass, plannedMass);

          return (
            <ColouredCell
              content={formatMass(level2Mass)}
              alpha={deviated ? 0.4 : 0}
              color={theme.palette.data.blue}
              justifyContent="center"
            />
          );
        },
      })
    );

    return [
      ...cols,
      ...materialColumns,
      {
        field: "total_charged",
        headerName: t("total"),
        width: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        valueGetter: (_: unknown, row: HeatSeries) =>
          getTotalMass(row, LEVEL2_SOURCE),
        valueFormatter: (value: number) => formatMass(value),
      },
    ];
  }, [
    level2MaterialNames,
    t,
    formatDate,
    formatMass,
    theme.palette.data.blue,
    materialDisplayName,
  ]);

  const handleOnClick: GridEventListener<"cellClick"> = useCallback(
    (params: GridCellParams<HeatSeries>) => {
      setSelectedHeatId(params.row.heat_id);
    },
    [setSelectedHeatId]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "97%",
        width: "fit-content",
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <DateRangeSelector />
        <ToggleSelector<MassUnit>
          selectedValue={massUnit}
          setSelectedValue={onMassUnitChange}
          availableValueLabels={{
            small_mass: units("small_mass", false),
            mass: units("mass", false),
          }}
        />
      </Stack>
      <HotkeyCompliantDataGrid
        rows={heats}
        columns={columns}
        onCellClick={handleOnClick}
        loading={isFetching}
        getRowId={(row) => row.heat_id}
        hideFooter
        getRowHeight={() => "auto"}
        columnHeaderHeight={52}
        showCellVerticalBorder={false}
        showColumnVerticalBorder
        pinnedColumns={PINNED_COLUMNS}
        sortModel={SORT_MODEL}
        disableColumnSorting
        sx={{
          flex: 1,
          minHeight: 0,
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "normal",
            lineHeight: "1.2rem",
            textAlign: "center",
          },
          "& .MuiDataGrid-row": {
            minHeight: "30px !important",
            cursor: "pointer",
          },
          "& .MuiDataGrid-columnHeader": {
            height: "auto !important",
            maxHeight: "none !important",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            maxHeight: "none !important",
          },
          "& .MuiDataGrid-cell.coloured-cell": {
            padding: 0,
          },
        }}
      />
      <TablePagination
        component="div"
        count={totalHeats}
        page={page}
        onPageChange={(_, newPage) => {
          onPageChange(newPage);
        }}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        sx={{ "& .MuiTablePagination-toolbar": { minHeight: 0, p: 0 } }}
      />
    </Box>
  );
};
